/**
 * Acting-role resolution for multi-role users (BAL-340 role switcher).
 *
 * The RoleSwitcher's displayed default is the FIRST profile role when no
 * explicit `ballee-active-role` cookie is set — so the menu must follow the
 * same precedence or the chrome contradicts the switcher ("Acting as Dancer"
 * while admin sections render). Acting roles are the COMPLETE partition of
 * the sidebar: one role's menu at a time, and the switcher is the only
 * crossover point (navigation-architecture skill).
 */

export interface ActingRoleContext {
  isSuperAdmin: boolean;
  /** Client-portal membership — adds the flag-derived `client` acting role. */
  isClientUser?: boolean;
  activeRole: string | null;
  roles: Array<{ profileType: string }>;
}

/**
 * The ordered role tokens the user can act as: their professional_profiles
 * rows first (switcher order), then the flag-derived extras by dominant-role
 * precedence (superadmin > client — mirrors resolve-roles.ts), so a pure super
 * admin with no cookie still defaults to the admin menu. Mirrors the extraRoles
 * the RoleSwitcher offers and the tokens setActiveRoleAction accepts.
 */
function actingRoleOptions(w: ActingRoleContext): string[] {
  const options: string[] = [];
  const seen = new Set<string>();
  const push = (role: string) => {
    if (seen.has(role)) return;
    seen.add(role);
    options.push(role);
  };

  for (const role of w.roles) push(role.profileType);
  if (w.isSuperAdmin) push('superadmin');
  if (w.isClientUser) push('client');

  return options;
}

/**
 * The role the user is currently acting as, resolved among their profile
 * roles + flag-derived extras. The fallback MUST stay in sync with the
 * RoleSwitcher's displayed default (role-switcher.tsx) so chrome and
 * switcher never contradict each other.
 */
export function resolveActingRole(w: ActingRoleContext): string {
  const options = actingRoleOptions(w);

  if (w.activeRole && options.includes(w.activeRole)) {
    return w.activeRole;
  }

  // No (valid) explicit choice: super admins default to the admin menu —
  // it is their workday surface; acting as Dancer/Client is the opt-in
  // preview (2026-07-05 feedback). Everyone else defaults to their first
  // profile role.
  if (w.isSuperAdmin) {
    return 'superadmin';
  }

  return w.roles[0]?.profileType ?? options[0] ?? 'dancer';
}

export function isActingSuperAdmin(w: ActingRoleContext): boolean {
  return w.isSuperAdmin && resolveActingRole(w) === 'superadmin';
}

/**
 * The workspace's `activeRole`: the user's EXPLICIT acting-role choice when it
 * is valid, else their default. Computed server-side by load-user-workspace
 * and fed to {@link resolveActingRole} by every navigation surface.
 *
 * Priority:
 *   1. the acting-role cookie, if the user holds that role;
 *   2. a super admin who never picked a role acts as Admin (`superadmin`),
 *      even when they also hold a dancer or other profile — the admin menu is
 *      their workday surface (2026-07-05 feedback; user decision 2026-10-02:
 *      before this, a super admin with a dancer profile defaulted to Dancer);
 *   3. the metadata primary role, if held;
 *   4. the first onboarded role, then any role;
 *   5. the metadata role even without a professional_profiles row, else null.
 */
export function resolveWorkspaceActiveRole(input: {
  cookieRole: string | null | undefined;
  metaRole: string;
  roles: Array<{ profileType: string; onboardedAt?: string | null }>;
  isSuperAdmin: boolean;
  isClientUser: boolean;
}): string | null {
  const held = new Set(input.roles.map((r) => r.profileType));
  if (input.isSuperAdmin) held.add('superadmin');
  if (input.isClientUser) held.add('client');

  if (input.cookieRole && held.has(input.cookieRole)) return input.cookieRole;
  if (input.isSuperAdmin) return 'superadmin';
  if (input.metaRole && held.has(input.metaRole)) return input.metaRole;

  return (
    input.roles.find((r) => r.onboardedAt)?.profileType ??
    input.roles[0]?.profileType ??
    (input.metaRole.length > 0 ? input.metaRole : null)
  );
}
